<template>
    <div class="flex flex-col mt-5">
        <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div
                class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
            >
                <table class="min-w-full divide-y divide-gray-200">
                    <thead>
                        <tr>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.title') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider flex justify-between"
                            >
                                <span>
                                    {{ $t('messages.description') }}
                                </span>
                                <div :title="$t('messages.information_visible_by_students')">
                                    <svg
                                        class="w-6 h-6 text-gray-300"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </div>
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.grouping') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.duration') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.to_be_graded') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                {{ $t('messages.max_score') }}
                            </th>
                            <th
                                class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                            >
                                <span class="sr-only">{{ $t('messages.actions') }}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr class="bg-white" v-for="(row, index) in activities" :key="index">
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <input
                                    type="text"
                                    :name="`activities[${index}][title]`"
                                    v-model="row.title"
                                    class="border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                                    id="title"
                                    required
                                    min="5"
                                />
                                <FormError :value="errors[`activities.${index}.title`]" class="mt-2 max-w-sm" />
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <input
                                    type="text"
                                    :name="`activities[${index}][description]`"
                                    v-model="row.description"
                                    class="alert-background-icon border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                                />
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <select
                                    id="grouping"
                                    :name="`activities[${index}][grouping]`"
                                    v-model="row.grouping"
                                    class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                >
                                    <option
                                        v-for="grouping in options.groupings"
                                        :value="grouping.value"
                                        v-text="grouping.text"
                                    />
                                </select>
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <input
                                    type="number"
                                    name="`activities[${index}][duration]`"
                                    v-model="row.duration"
                                    class="w-20 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                                />
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <input
                                    type="checkbox"
                                    :name="`activities[${index}][weight]`"
                                    v-model="row.weight"
                                    :true-value="2"
                                    :false-value="0"
                                    class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                                />
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <input
                                    type="number"
                                    v-show="row.weight === 2"
                                    :name="`activities[${index}][max_score]`"
                                    id="max_score"
                                    v-model="row.max_score"
                                    required
                                    min="1"
                                    max="90"
                                    step="1"
                                    class="w-20 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:max-w-xs sm:text-sm"
                                />
                            </td>
                            <td
                                class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                            >
                                <button
                                    type="button"
                                    @click="deleteRow(index)"
                                    class="inline-flex items-center px-1.5 py-1.5 border border-transparent text-xs font-medium rounded text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                >
                                    <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                        <path
                                            fill-rule="evenodd"
                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="mt-3">
                <button
                    type="button"
                    @click="addRow()"
                    class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <svg
                        class="h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        aria-hidden="true"
                    >
                        <path
                            fill-rule="evenodd"
                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                            clip-rule="evenodd"
                        />
                    </svg>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import FormError from '@/components/Forms/FormError.vue';

export default {
    emits: ['update:modelValue'],

    components: {
        FormError,
    },

    props: ['modelValue', 'options', 'defaults', 'errors'],
    name: 'ActivityBatchCreateTable',

    data() {
        return {
            activities: this.modelValue,
        };
    },

    methods: {
        addRow() {
            this.activities.push({
                title: '',
                description: '',
                grouping: null,
                weight: 0,
                max_score: this.defaults.max_score,
                duration: null,
            });
        },
        deleteRow(index) {
            this.activities.splice(index, 1);
            if (this.activities.length === 0) {
                this.addRow();
            }
        },
    },

    watch: {
        activities(newVal) {
            this.$emit('update:modelValue', newVal);
        },
    },

    mounted() {
        if (this.activities.length === 0) {
            this.addRow();
        }
    },
};
</script>
